import { useEffect, useState } from "react";
import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import { formatCount } from "@/lib/format-count";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { UserProfileMenu } from "@/components/panels/user-profile-menu";
import {
  utilityButtonClass,
  verticalTooltipClass,
} from "@/components/panels/app-utility-control-styles";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icons } from "@/components/ui/icons";
import { surfaceHoverClassName } from "@/components/ui/surface-contract";
import { useNotificationStore } from "@/stores/notification-store";
import { useThemeCycle } from "@/hooks/use-theme-cycle";
import { cn } from "@/lib/cn";

type UtilityOrientation = "horizontal" | "vertical";

interface AppUtilityControlsProps {
  orientation?: UtilityOrientation;
  /**
   * When false, any open notification / user-profile popover is force-closed.
   * Used by the desktop rail so popovers can't linger over compact mode after
   * a mode switch (the rail itself is only hidden via opacity, never unmounted).
   */
  railVisible?: boolean;
  onSearch?: () => void;
  onShortcuts?: () => void;
  showUserMenu?: boolean;
  userLetter?: string;
  onOpenNotifications?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
  testIdPrefix?: string;
}

function tooltip(
  label: string,
  orientation: UtilityOrientation,
  hidden = false,
) {
  if (orientation !== "vertical") return null;
  return (
    <span
      className={verticalTooltipClass}
      data-suppressed={String(hidden)}
      aria-hidden={hidden || undefined}
      inert={hidden || undefined}
    >
      {label}
    </span>
  );
}

export function AppUtilityControls({
  orientation = "horizontal",
  railVisible = true,
  onSearch,
  onShortcuts,
  showUserMenu = true,
  userLetter = "D",
  onOpenNotifications,
  onSettings,
  onLogout,
  testIdPrefix = "header",
}: AppUtilityControlsProps) {
  const t = useT(panelsHeaderMessages);
  const notificationCount = useNotificationStore((s) => s.summary.count);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const {
    theme,
    options: themeOptions,
    active: activeThemeOption,
    select: selectTheme,
    cycle: cycleTheme,
  } = useThemeCycle();
  const vertical = orientation === "vertical";
  const suppressRailTooltips = vertical && userMenuOpen;

  // Force-close popovers when the rail is hidden (e.g. entering compact mode).
  // Both render at the top of the DOM (portal / Radix), so they'd otherwise
  // linger fully opaque over the compact overlay after a mode switch.
  useEffect(() => {
    if (!railVisible) {
      setUserMenuOpen(false);
    }
  }, [railVisible]);

  const ActiveThemeIcon = activeThemeOption.icon;
  const themeTestId =
    testIdPrefix === "header" ? "theme-toggle" : `${testIdPrefix}-theme-toggle`;
  const settingsTestId =
    testIdPrefix === "header"
      ? "user-menu-settings"
      : `${testIdPrefix}-user-menu-settings`;
  const logoutTestId =
    testIdPrefix === "header"
      ? "user-menu-logout"
      : `${testIdPrefix}-user-menu-logout`;

  return (
    <div
      className={cn("flex items-center gap-1.5", vertical && "flex-col gap-1")}
      data-testid={`${testIdPrefix}-utility-controls`}
    >
      <Button
        variant="ghost"
        size="icon"
        className={cn(utilityButtonClass, vertical && "group relative")}
        onClick={onSearch}
        data-testid={`${testIdPrefix}-search`}
        aria-label={t("search")}
      >
        <Icons.Search className="h-4 w-4" />
        {tooltip(t("search"), orientation, suppressRailTooltips)}
      </Button>

      {onShortcuts && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(utilityButtonClass, vertical && "group relative")}
          onClick={onShortcuts}
          data-testid={`${testIdPrefix}-shortcuts`}
          aria-label={t("keyboardShortcuts")}
        >
          <Icons.Info className="h-4 w-4" aria-hidden="true" />
          {tooltip(t("keyboardShortcuts"), orientation, suppressRailTooltips)}
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={onOpenNotifications}
        className={cn(utilityButtonClass, "relative", vertical && "group")}
        data-testid={`${testIdPrefix}-notifications`}
        aria-label={
          notificationCount > 0
            ? t("notificationsNew", { total: formatCount(notificationCount) })
            : t("notifications")
        }
      >
        <CONCEPT_ICONS.notification className="h-4 w-4" aria-hidden="true" />
        {notificationCount > 0 ? (
          <span
            className={cn(
              "absolute font-medium text-muted tabular-nums",
              vertical
                ? "left-[calc(50%+8px)] top-1 text-[8px] leading-none"
                : "-right-1 -top-0.5 text-appkit-mini",
            )}
            data-testid={`${testIdPrefix}-notification-count`}
          >
            {formatCount(notificationCount)}
          </span>
        ) : null}
        {tooltip(t("notifications"), orientation, suppressRailTooltips)}
      </Button>

      {vertical ? (
        <Button
          variant="ghost"
          size="icon"
          className={cn(utilityButtonClass, "group relative")}
          data-testid={themeTestId}
          aria-label={t("themeLabel", { theme: activeThemeOption.label })}
          onClick={cycleTheme}
        >
          <ActiveThemeIcon className="h-4 w-4" />
          {tooltip(activeThemeOption.label, orientation, suppressRailTooltips)}
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={utilityButtonClass}
              data-testid={themeTestId}
              aria-label={t("themeLabel", { theme: activeThemeOption.label })}
            >
              <ActiveThemeIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="bottom"
            className="min-w-44"
            data-testid={`${testIdPrefix}-theme-menu-content`}
            data-interactive="true"
          >
            {themeOptions.map((option) => {
              const OptionIcon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => selectTheme(option.id)}
                  data-testid={`${testIdPrefix}-theme-${option.id}`}
                >
                  <OptionIcon className="h-3.5 w-3.5" />
                  <span className="flex-1">{option.label}</span>
                  {theme === option.id && (
                    <Icons.Check className="h-3.5 w-3.5" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {!vertical && <div className="w-0.5 shrink-0" aria-hidden="true" />}

      {showUserMenu &&
        (vertical ? (
          <div className="group relative">
            <UserProfileMenu
              userLetter={userLetter}
              onSettings={onSettings}
              className="h-9 w-9 bg-transparent text-appkit-caption"
              side="right"
              align="start"
              open={userMenuOpen}
              onOpenChange={setUserMenuOpen}
              data-settings-anchor=""
              data-testid={`${testIdPrefix}-user-menu`}
            />
            {tooltip(t("userMenu"), orientation, suppressRailTooltips)}
          </div>
        ) : (
          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  surfaceHoverClassName(),
                  "group relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent outline-none hover:animate-wobble",
                )}
                data-settings-anchor=""
                data-testid={`${testIdPrefix}-user-menu`}
                aria-label={t("userMenu")}
              >
                <span className="text-appkit-subhead font-semibold text-primary">
                  {userLetter}
                </span>
                {tooltip(t("userMenu"), orientation, suppressRailTooltips)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={vertical ? "start" : "end"}
              side={vertical ? "right" : "bottom"}
              className="min-w-40"
              data-testid={`${testIdPrefix}-user-menu-content`}
              data-interactive="true"
            >
              <DropdownMenuItem
                onClick={onSettings}
                data-testid={settingsTestId}
              >
                <Icons.Settings className="h-3.5 w-3.5" />
                {t("settings")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onLogout}
                destructive
                data-testid={logoutTestId}
              >
                <Icons.LogOut className="h-3.5 w-3.5" />
                {t("logOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
    </div>
  );
}

export type { AppUtilityControlsProps };
